import { useQuery } from "@tanstack/react-query";
import api, { getImageUrl } from "../../services/api";

export default function Reviews() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["reviews"],
    queryFn: () => api.get("/reviews").then(res => res.data),
  });

  if (isLoading) return (
    <div className="text-center py-12 text-gray-600">
      Loading reviews<span className="animate-pulse">...</span>
    </div>
  );
 
  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          Error loading reviews: {error?.response?.data?.message || "Something went wrong"}
        </div>
      </div>
    );
  }

  const reviews = data?.reviews || [];
  const total = data?.total || 0;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 mb-6">
      <div className="flex justify-between items-center mb-6 pb-6 border-b border-gray-200 flex-wrap gap-4">
        <h2 className="text-2xl font-bold text-gray-900">User Reviews</h2>
        {total > 0 && (
          <span className="text-gray-600 text-base">
            Total: {total} review{total !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {reviews.length > 0 ? (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 min-w-[200px] max-w-[300px]">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 min-w-[150px]">User Details</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 w-24">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 min-w-[200px] max-w-[300px]">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 w-28">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-gray-200 w-40">Date</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((item) => {
                  const review = item.review || {};
                  const user = item.user || {};

                  return (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4 border-b border-gray-200">
                        <div className="flex items-start gap-2.5">
                          {review.product_image && (
                            <img
                              src={getImageUrl(review.product_image)}
                              alt={review.product_name}
                              className="w-12 h-12 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-900 text-sm break-words line-clamp-2">
                              {review.product_name || "N/A"}
                            </div>
                            <div className="text-xs text-gray-500 mt-1 font-mono">
                              ASIN: {review.asin || "N/A"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200">
                        <div className="space-y-1">
                          <div className="text-xs text-gray-600 break-all">{user.email || "N/A"}</div>
                          <div className="text-xs text-gray-600">{user.phone || "N/A"}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200">
                        {review.rating ? (
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                              review.rating >= 4
                                ? "bg-green-100 text-green-800"
                                : review.rating >= 3
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {review.rating} ⭐
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200">
                        {review.message ? (
                          <div className="text-xs text-gray-700 max-w-[300px] line-clamp-3" title={review.message}>
                            {review.message}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs italic">No message</span>
                        )}
                      </td>
                      <td className="px-4 py-4 border-b border-gray-200">
                        {review.status ? (
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium uppercase tracking-wider ${
                              review.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {review.status}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600 border-b border-gray-200 whitespace-nowrap">
                        {formatDate(review.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block md:hidden space-y-4">
            {reviews.map((item) => {
              const review = item.review || {};
              const user = item.user || {};

              return (
                <div key={item.id} className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    {review.status && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium uppercase tracking-wider ${
                          review.status === "completed"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {review.status}
                      </span>
                    )}
                    {review.rating && (
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                          review.rating >= 4
                            ? "bg-green-100 text-green-800"
                            : review.rating >= 3
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {review.rating} ⭐
                      </span>
                    )}
                  </div>

                  <div className="flex items-start gap-3 pb-3 border-b border-gray-200">
                    {review.product_image && (
                      <img
                        src={getImageUrl(review.product_image)}
                        alt={review.product_name}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm mb-1 break-words">
                        {review.product_name || "N/A"}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        ASIN: {review.asin || "N/A"}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 pb-3 border-b border-gray-200">
                    <div className="text-xs font-semibold text-gray-500 uppercase">User Details</div>
                    <div className="text-xs text-gray-600 break-all">{user.email || "N/A"}</div>
                    <div className="text-xs text-gray-600">{user.phone || "N/A"}</div>
                  </div>

                  {review.message && (
                    <div className="space-y-2 pb-3 border-b border-gray-200">
                      <div className="text-xs font-semibold text-gray-500 uppercase">Review Message</div>
                      <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded border border-gray-200">
                        {review.message}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-gray-500">
                    {formatDate(review.created_at)}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="text-center py-16 px-4">
          <div className="w-20 h-20 mx-auto mb-6 opacity-40 text-gray-400">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-full h-full">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">No reviews yet</h3>
          <p className="text-gray-500 text-sm max-w-md mx-auto">User reviews will appear here once customers submit their feedback through the review form</p>
        </div>
      )}
    </div>
  );
}
